import React, { useMemo } from 'react';
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../utils/cn';
import { DauReportsResponse } from '../../types/reports';
import { format, parseISO } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

interface EngagementMetricModalProps {
  data: DauReportsResponse;
  comparisonData?: DauReportsResponse | null;
  isOpen: boolean;
  onClose: () => void;
}

interface ChartDataPoint {
  date: string;
  dateLabel: string;
  dau: number;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="font-semibold text-gray-900 mb-2">
        {format(parseISO(data.date), 'MMM d, yyyy')}
      </p>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-700">Daily Active Users:</span>
        <span className="text-sm font-semibold text-gray-900 ml-3">
          {data.dau} {data.dau === 1 ? 'user' : 'users'}
        </span>
      </div>
    </div>
  );
};

export const EngagementMetricModal: React.FC<EngagementMetricModalProps> = ({
  data,
  comparisonData,
  isOpen,
  onClose
}) => {
  // Prepare chart data - must be before early return
  const chartData: ChartDataPoint[] = useMemo(() => {
    return data.data.map(metric => ({
      date: metric.Date,
      dateLabel: format(parseISO(metric.Date), 'MMM d'),
      dau: metric.DAU
    }));
  }, [data.data]);

  // Calculate statistics
  const { avgDAU, maxDAU, registeredUsers, engagementRate } = useMemo(() => {
    const dauValues = data.data.map(m => m.DAU);
    const avgDAU = dauValues.length > 0 
      ? dauValues.reduce((sum, count) => sum + count, 0) / dauValues.length
      : 0;
    const maxDAU = dauValues.length > 0 ? Math.max(...dauValues) : 0;
    const registeredUsers = data.summary?.statistics?.totalUniqueUsers || 20;
    const engagementRate = registeredUsers > 0 ? (avgDAU / registeredUsers) * 100 : 0;

    return { avgDAU, maxDAU, registeredUsers, engagementRate };
  }, [data]);

  // Calculate comparison stats
  const comparisonStats = useMemo(() => {
    if (!comparisonData || comparisonData.data.length === 0) return null;

    const comparisonDauValues = comparisonData.data.map(m => m.DAU);
    const comparisonAvgDAU = comparisonDauValues.reduce((sum, count) => sum + count, 0) / comparisonDauValues.length;
    const comparisonRegisteredUsers = comparisonData.summary?.statistics?.totalUniqueUsers || registeredUsers;
    const comparisonEngagementRate = comparisonRegisteredUsers > 0 
      ? (comparisonAvgDAU / comparisonRegisteredUsers) * 100 
      : 0;

    const trend = avgDAU - comparisonAvgDAU;
    const engagementTrend = engagementRate - comparisonEngagementRate;
    const trendType: 'up' | 'down' | 'stable' = trend > 0 ? 'up' : trend < 0 ? 'down' : 'stable';

    return {
      avgDAU: comparisonAvgDAU,
      engagementRate: comparisonEngagementRate,
      trend,
      engagementTrend,
      trendType
    };
  }, [comparisonData, avgDAU, engagementRate, registeredUsers]);

  if (!isOpen) return null;

  const TrendIcon = comparisonStats?.trendType === 'up' 
    ? TrendingUp 
    : comparisonStats?.trendType === 'down' 
      ? TrendingDown 
      : Minus;

  const trendColor = comparisonStats?.trendType === 'up' 
    ? 'text-green-600' 
    : comparisonStats?.trendType === 'down' 
      ? 'text-red-600' 
      : 'text-rm-neutral-600';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-card shadow-card max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-rm-neutral-600/10">
            <div>
              <h2 className="text-xl font-semibold text-rm-primary-700">
                EAP Engagement Rate
              </h2>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-2xl font-bold text-rm-ink-900">
                  {engagementRate.toFixed(1)}%
                </span>
                {comparisonStats && Math.abs(comparisonStats.engagementTrend) >= 0.01 && (
                  <div className={cn('flex items-center gap-1', trendColor)}>
                    <TrendIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {comparisonStats.engagementTrend > 0 ? '+' : ''}{comparisonStats.engagementTrend.toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>
              <p className="text-sm text-rm-neutral-600 mt-1">
                Avg {Math.round(avgDAU)} active users / {registeredUsers} registered · {data.summary.totalDays} days
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-rm-bg-50 text-rm-neutral-600 hover:text-rm-ink-900 transition-colors self-start ml-auto"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 overflow-y-auto max-h-[calc(90vh-120px)]">
            {chartData.length > 0 ? (
              <div>
                <h3 className="text-sm font-semibold text-rm-ink-900 mb-4">
                  Daily Active Users ({format(parseISO(data.summary.dateRange.startDate), 'MMM d')} – {format(parseISO(data.summary.dateRange.endDate), 'MMM d, yyyy')})
                </h3>
                
                {/* Stats Summary */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-rm-bg-50 rounded-lg px-3 py-2 border border-rm-neutral-600/10 flex items-center justify-between gap-2">
                    <p className="text-sm text-rm-neutral-600">Average DAU</p>
                    <p className="text-sm font-semibold text-rm-ink-900">{avgDAU.toFixed(1)}</p>
                  </div>
                  <div className="bg-rm-bg-50 rounded-lg px-3 py-2 border border-rm-neutral-600/10 flex items-center justify-between gap-2">
                    <p className="text-sm text-rm-neutral-600">Peak DAU</p>
                    <p className="text-sm font-semibold text-rm-ink-900">{maxDAU}</p>
                  </div>
                  <div className="bg-rm-bg-50 rounded-lg px-3 py-2 border border-rm-neutral-600/10 flex items-center justify-between gap-2">
                    <p className="text-sm text-rm-neutral-600">Registered Users</p>
                    <p className="text-sm font-semibold text-rm-ink-900">{registeredUsers}</p>
                  </div>
                </div>

                <ResponsiveContainer width="100%" height={400}>
                  <LineChart
                    data={chartData}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis 
                      dataKey="dateLabel" 
                      stroke="#6b7280"
                      fontSize={12}
                      tickLine={false}
                    />
                    <YAxis 
                      stroke="#6b7280"
                      fontSize={12}
                      tickLine={false}
                      domain={[0, 'dataMax + 1']}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    
                    {/* Reference line for average */}
                    {avgDAU > 0 && (
                      <ReferenceLine 
                        y={avgDAU} 
                        stroke="#f59e0b" 
                        strokeDasharray="2 2"
                        label={{ value: `Avg: ${Math.round(avgDAU)}`, position: 'left', fontSize: 12 }}
                      />
                    )}
                    
                    <Line
                      type="monotone"
                      dataKey="dau"
                      name="Daily Active Users"
                      stroke="#3b82f6"
                      strokeWidth={3}
                      dot={{ 
                        fill: '#3b82f6',
                        strokeWidth: 2,
                        r: 5
                      }}
                      connectNulls={false}
                      activeDot={{ r: 7, stroke: '#3b82f6', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No engagement data available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementMetricModal;
